import { ColDef } from "ag-grid-community";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { FormEvent, useState } from "react";
import { request } from "../../common/api";
import { CurrentShip, currentShipState } from "../../common/states/ship";
import useShipSearchInputData from "../../common/hooks/useShipOperaionSearchInputData";
import {
  EquipmentOperationStatus,
  EquipmentOperationStatusLED,
} from "../../common/types/equipment";
import {
  GET_EQUIPMENT_ALARM_STATUS_ALL,
  GET_EQUIPMENT_OPERATION_STATUS,
  GET_EQUIPMENT_OPERATION_STATUS_LED,
} from "../../common/query-keys";
import { Grid } from "../../components/molecules/Grid";
import { Legend } from "../../components/molecules/Legends";
import { Loading } from "../../components/atoms/Loading";
import { ModalDDU } from "../../components/molecules/Modals/ModalDDU";
import { ModalVDS } from "../../components/molecules/Modals/ModalVDS";
import { NoDataBox } from "../../components/molecules/NoDataBox";
import { PageContent } from "../../components/organisms/Contents/PageContent";
import { SystemSection } from "../../components/organisms/SystemSection";
import { LedLegendItem } from "../../components/atoms/LegendItem/LedLegendItem";
import DiagnosisContextMenu from "../../components/molecules/DiagnosisContextMenu";
import useDiagnosisContextMenu from "../../common/hooks/useDiagnosisContextMenu";

export const AllPage = () => {
  /** ============================= state 영역 ============================= */
  const navigate = useNavigate();

  // 현재 함정 정보
  const currentShip: CurrentShip = useRecoilValue(currentShipState);
  const { contextMenu, handleContextMenu, handleOpenInNewTab } =
    useDiagnosisContextMenu();

  /** ============================= API 영역 ============================= */

  /**
   * 장비 운용현황 조회
   * ? fetch 코드 수정 완료 2025.05.23
   * ! 백엔드 메인 쿼리 수정 필요
   */
  const { data: equipmentOperationStatus, isFetching } = useQuery({
    queryKey: [GET_EQUIPMENT_OPERATION_STATUS],
    queryFn: async () => {
      return await request<EquipmentOperationStatus>({
        method: "GET",
        url: `/equipment/operation/status/${currentShip.shipNo}`,
      });
    },
  });

  /**
   * 전체 장비 운용의 장비 버튼 색( 해당 장비에 속한 센서의 worst case)
   * * 수정 완료
   */
  const { data: equipmentOperationStatusLED } = useQuery({
    queryKey: [
      GET_EQUIPMENT_OPERATION_STATUS_LED,
      currentShip.shipTypeNo,
      currentShip.shipNo,
    ],
    queryFn: async () => {
      return await request<EquipmentOperationStatusLED>({
        method: "GET",
        url: `/equipment/operation/status/led`,
        params: {
          steShiptypeNo: currentShip.shipTypeNo,
          steShipNo: currentShip.shipNo,
        },
      });
    },
  });

  /** 장비모니터링> 장비운용현황 > 알람현황전체
   * * 수정완료 - 이전의 알람 현황 limit 1000 그리드 적용
   */
  const {
    data: alarmStatusBySystemDataSource,
    isFetching: alarmStatusIsLoading,
  } = useQuery({
    queryKey: [
      GET_EQUIPMENT_ALARM_STATUS_ALL,
      currentShip.shipTypeNo,
      currentShip.shipNo,
    ],
    queryFn: async () => {
      return await request<any>({
        method: "GET",
        url: `/equipment-monitoring/alarm-status-all`,
        params: {
          steShiptypeNo: currentShip.shipTypeNo,
          steShipNo: currentShip.shipNo,
        },
      }).then((res: any) => {
        return res.data;
      });
    },
  });

  /** ============================= 비즈니스 로직 영역 ============================= */

  const navigateToPath = (path?: string) => {
    console.log(path);
    if (path) {
      navigate(path);
    }
  };

  /** ============================= 컴포넌트 영역 ============================= */
  const legendItemRenderer = (params: any) => {
    const alarmType = params.data.ste_alarm_type;

    switch (alarmType) {
      case 0:
        return (
          <LedLegendItem
            ledColor="green"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      case 1:
        return (
          <LedLegendItem
            ledColor="yellow"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      case 2:
        return (
          <LedLegendItem
            ledColor="red"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      default:
        return;
      // return <LedLegendItem ledColor="green" title="" />;
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "레벨",
      field: "level",

      cellRenderer: legendItemRenderer,
    },
    {
      headerName: "진단 시간",
      field: "steAlarmTime",
    },
    {
      headerName: "장비",
      field: "ste_mcy_name",
    },
    {
      headerName: "계통",
      field: "ste_low_mcy_name",
    },
    {
      headerName: "센서",
      field: "ste_sensor_code",
    },
    {
      headerName: "센서 코드",
      field: "ste_sensor_name",
      flex: 1,
    },
    {
      headerName: "알림 메시지",
      field: "ste_alarm_msg",
      flex: 1,
    },
  ];
  /** ============================= useEffect 영역 ============================= */
  return (
    <>
      <PageContent depth01Title="장비 모니터링" depth02Title="전체 장비운용">
        {isFetching ? (
          <Loading />
        ) : !currentShip.shipId ? (
          <NoDataBox title="함정을 선택해 주세요." />
        ) : !equipmentOperationStatus ? (
          <NoDataBox subtitle="다른 함정을 선택해 주세요." />
        ) : (
          <div className="content-init">
            <Legend
              type="sailing-info"
              title="함정 운항정보"
              shipOperationStatus={equipmentOperationStatus}
            />
            {/* 전개도 그림  */}
            <SystemSection
              shipType={currentShip?.shipTypeName || "1000"}
              equipmentOperationStatus={equipmentOperationStatus}
              equipmentOperationStatusLED={equipmentOperationStatusLED}
              navigateToPath={navigateToPath}
              onContextMenu={handleContextMenu}
            />
            <Legend type="led-alarm" />
            <Grid
              columnDefs={columnDefs}
              rowData={alarmStatusBySystemDataSource}
              height={200}
              paging={false}
              pagingShow={false}
              loading={alarmStatusIsLoading}
            />
          </div>
        )}
      </PageContent>

      <DiagnosisContextMenu
        handleOpenInNewTab={handleOpenInNewTab}
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
      />
    </>
  );
};
